<script setup lang="ts">
import { computed, inject, ref, useTemplateRef } from "vue";
import { storeToRefs } from "pinia";

import Menu from "primevue/menu";

import SidebarLabelMenuItem from "@/components/navigation/SidebarLabelMenuItem.vue";
import SidebarRouterMenuItem from "@/components/navigation/SidebarRouterMenuItem.vue";
import SidebarActionMenuItem from "@/components/navigation/SidebarActionMenuItem.vue";
import SignoutModal from "@/components/modals/SignoutModal.vue";

import { useProductsStore } from "@/stores/Products";
import { useUserStore } from "@/stores/User";

import { useMenuPermissions } from "@/composables/menu-permissions";
import { useVersionInfo } from "@/services/version-service";

import type { LabelService } from "@/composables/label-service";
import type { NavGroup, NavItem } from "@/types/navigation";
import type { Product } from "@/types/entities";

import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";
import routeManager from "@/router/route-manager";

import { ConfigurationError } from "@/types/errors";
import { SimpleRoutingParams } from "@/types/routing";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const legal = routeManager.legalNavigation();
const privacyPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblPrivacyPolicy' )! );
const refundPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblRefundPolicy' )! );
const smsTextPolicy = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblSmsPolicy' )! );
const termsAndConditions = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblTermsAndConditions' )! );
const whitelistInstructions = ref< SimpleRoutingParams >( findRoutingConfig( legal, 'lblWhitelistInstructions' )! );

const social = routeManager.socialNavigation();
const facebook = ref< SimpleRoutingParams >( findRoutingConfig( social, 'lblFacebook' )! );
const x = ref< SimpleRoutingParams >( findRoutingConfig( social, 'lblX' )! );
const instagram = ref< SimpleRoutingParams >( findRoutingConfig( social, 'lblInstagram' )! );
const youTube = ref< SimpleRoutingParams >( findRoutingConfig( social, 'lblYouTube' )! );
const discord = ref< SimpleRoutingParams >( findRoutingConfig( social, 'lblDiscord' )! );

function findRoutingConfig( source: SimpleRoutingParams[], itemKey: string ): SimpleRoutingParams {
  const config = source.find( ( { key } ) => key === itemKey )!;
  if ( !config ) {
    throw new ConfigurationError(
        `The route configuration with key '${ itemKey }' is not currently defined. Update the definition in routeManager and rebuild.`
    )
  }

  return config;
}

const productsStore = useProductsStore();
const { availableProducts, subscribedProducts } = storeToRefs( productsStore );
const userStore = useUserStore();
const {
  // isAllianceMember,
  showBonusReportOption,
  showCoursesMenuOption,
  showEtfListingMenuOption,
  showLearningMenuItem,
  showMajorIndexBreadthMenuOption,
  showNyseBpiMenuOption,
  showOptionsCalculatorMenuOption,
  showPortfolioBuilderMenuOption,
  showPositionSizeCalculatorMenuOption,
  showRelativeStrengthMatrixMenuOption,
  showSectorAlertsMenuOption,
  showSectorBpiMenuOption,
  showSectorsMenuOption,
  showSectorTreeMenuOption,
  showSpreadCalculatorMenuOption,
  showToolsMenuItem,
  showTradeAlertsMenuOption,
  showWebinarsMenuOption,
  showAlertsMenuItem,
  showArticlesOption
} = useMenuPermissions();

const versionLabel = ref<string>(
    `v${ useVersionInfo().currentVersion }`
);

const productsNavItems = computed< NavItem[] >( () => {
  let products: NavItem[] = [];

/*
  if ( !isAllianceMember.value ) {
    products.push( {
      label: labelService.label( 'lblAlliance' ),
      route: '/',
      icon: 'pi-lock',
      visible: true
    } );
  }
*/

  subscribedProducts.value.forEach( ( product: Product ) => {
    products.push( {
      label: product.name,
      route: `/products/${ product.slug }`,
      image: product.favicon,
      visible: true
    } );
  } );

  availableProducts.value.forEach( ( product: Product ) => {
    products.push( {
      label: product.name,
      route: `/products/${ product.slug }`,
      icon: 'pi-lock',
      visible: true
    } );
  } );

  return products;
} );

const recurlyHostedLink = computed< string | undefined >( () => userStore.recurlyHostedLink );

const signoutModalRef = useTemplateRef( 'signoutModal' );

function showSignoutModal() {
  signoutModalRef.value?.open();
}

const navigation = computed< NavGroup[] >( () => {
      return [ {
        id: "home-menu",
        visible: true,
        items: [ {
          visible: true,
          label: labelService.label( 'lblDashboard' ),
          route: routeManager.homePath(),
          icon: "pi-gauge"
        } ]
      }, {
        label: labelService.label( 'lblServices' ),
        id: "subscriptions-menu",
        visible: true,
        items: productsNavItems.value
      }, {
        label: labelService.label( 'lblMarketAlerts' ),
        id: "alerts-menu",
        visible: showAlertsMenuItem.value,
        items: [ {
          label: labelService.label( 'lblSectorAlerts' ),
          route: routeManager.sectorAlertsPath(),
          visible: showSectorAlertsMenuOption.value,
          icon: "pi-bolt"
        }, {
          label: labelService.label( 'lblTradeAlerts' ),
          route: routeManager.tradeAlertsPath(),
          visible: showTradeAlertsMenuOption.value,
          icon: "pi-chart-line"
        } ]
      }, {
        label: labelService.label( 'lblLearning' ),
        id: "learning-menu",
        visible: showLearningMenuItem.value,
        items: [ {
          label: labelService.label( 'lblArticles' ),
          route: routeManager.articleSearchPath(),
          visible: showArticlesOption.value,
          icon: "pi-folder"
        }, {
          label: labelService.label( 'lblBonusReports' ),
          route: routeManager.bonusReportsSearchPath(),
          visible: showBonusReportOption.value,
          icon: "pi-file-pdf"
        }, {
          label: labelService.label( 'lblCourses' ),
          route: routeManager.coursesPath(),
          visible: showCoursesMenuOption.value,
          icon: "pi-graduation-cap"
        }, {
          label: labelService.label( 'lblMedia' ),
          route: routeManager.mediaPath(),
          visible: true,
          icon: "pi-desktop"
        }, {
          label: labelService.label( 'lblWebinars' ),
          route: routeManager.webinarSearchPath(),
          visible: showWebinarsMenuOption.value,
          icon: "pi-video"
        } ]
      }, {
        label: labelService.label( 'lblTools' ),
        id: "tools-menu",
        visible: showToolsMenuItem.value,
        items: [ {
          label: labelService.label( 'lblSectorBpis' ),
          route: routeManager.sectorBullishPercentIndicesPath(),
          visible: showSectorBpiMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblMajorIndexBreadth' ),
          route: routeManager.majorIndexBreadthPath(),
          visible: showMajorIndexBreadthMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblNyseBpi' ),
          route: routeManager.nyseBullishPercentIndexPath(),
          visible: showNyseBpiMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblOptionsCalculator' ),
          route: routeManager.optionsCalculatorPath(),
          visible: showOptionsCalculatorMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblPortfolioBuilder' ),
          route: routeManager.portfolioBuilderPath(),
          visible: showPortfolioBuilderMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblPositionSizeCalculator' ),
          route: routeManager.positionSizeCalculatorPath(),
          visible: showPositionSizeCalculatorMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblRsMatrix' ),
          route: routeManager.relativeStrengthMatrixPath(),
          visible: showRelativeStrengthMatrixMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblSectors' ),
          route: routeManager.sectorListingPath(),
          visible: showSectorsMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblSectorTree' ),
          route: routeManager.sectorTreePath(),
          visible: showSectorTreeMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblSpreadCalculators' ),
          route: routeManager.spreadCalculatorPath(),
          visible: showSpreadCalculatorMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblSpreadAnalyzer' ),
          route: routeManager.spreadAnalyzerPath(),
          visible: showOptionsCalculatorMenuOption.value,
          icon: "pi-expand"
        }, {
          label: labelService.label( 'lblEtfListing' ),
          route: routeManager.etfListingPath(),
          visible: showEtfListingMenuOption.value,
          icon: "pi-expand"
        } ]
      }, {
        label: labelService.label( 'lblMyAccount' ),
        id: "account-menu",
        visible: true,
        items: [ {
            label: labelService.label( 'lblProfile' ),
            visible: true,
            route: routeManager.profileBasePath(),
            icon: "pi-user"
          }, {
            label: labelService.label( 'lblBilling' ),
            visible: !!recurlyHostedLink.value,
            icon: "pi-credit-card",
            action: () => window.open( recurlyHostedLink.value, '_blank' )
          }, {
            label: labelService.label( 'lblMyLibrary' ),
            visible: true,
            route: routeManager.myLibraryPath(),
            icon: "pi-book"
          }, {
            label: labelService.label( 'lblSignOut' ),
            visible: true,
            icon: "pi-sign-out",
            action: () => showSignoutModal()
          }
        ]
      }, {
        label: labelService.label( 'lblSupport' ),
        id: "support-menu",
        visible: true,
        items: [ {
          label: labelService.label( 'lblAppStatus' ),
          visible: true,
          route: routeManager.appStatusPath(),
          icon: "pi-flag"
        }, {
          label: labelService.label( 'lblFeatureNews' ),
          visible: true,
          route: routeManager.featureNewsPath(),
          icon: "pi-megaphone"
        }, {
          label: labelService.label( 'lblContactUs' ),
          visible: true,
          route: routeManager.contactUsPath(),
          icon: "pi-phone"
        }, {
          label: labelService.label( whitelistInstructions.value.key ),
          visible: true,
          route: whitelistInstructions.value.path,
          icon: whitelistInstructions.value.icon
        }, {
          label: labelService.label( 'lblReset' ),
          visible: true,
          route: routeManager.appToolsPath(),
          icon: "pi-eraser"
        } ]
      }, {
        label: labelService.label( 'lblPolicies' ),
        id: "policy-menu",
        visible: true,
        items: [ {
          label: labelService.label( privacyPolicy.value.key ),
          visible: true,
          route: privacyPolicy.value.path,
          icon: privacyPolicy.value.icon
        }, {
          label: labelService.label( refundPolicy.value.key ),
          visible: true,
          route: refundPolicy.value.path,
          icon: refundPolicy.value.icon
        }, {
          label: labelService.label( smsTextPolicy.value.key ),
          visible: true,
          route: smsTextPolicy.value.path,
          icon: smsTextPolicy.value.icon
        }, {
          label: labelService.label( termsAndConditions.value.key ),
          visible: true,
          route: termsAndConditions.value.path,
          icon: termsAndConditions.value.icon
        }, ]
      }, {
        label: labelService.label( 'lblSocial' ),
        id: "social-menu",
        visible: true,
        items: [ {
          label: labelService.label( facebook.value.key ),
          visible: true,
          icon: facebook.value.icon,
          action: () => window.open( facebook.value.path, '_blank' )
        }, {
          label: labelService.label( x.value.key ),
          visible: true,
          icon: x.value.icon,
          action: () => window.open( x.value.path, '_blank' )
        }, {
          label: labelService.label( instagram.value.key ),
          visible: true,
          icon: instagram.value.icon,
          action: () => window.open( instagram.value.path, '_blank' )
        }, {
          label: labelService.label( youTube.value.key ),
          visible: true,
          icon: youTube.value.icon,
          action: () => window.open( youTube.value.path, '_blank' )
        }, {
          label: labelService.label( discord.value.key ),
          visible: true,
          icon: discord.value.icon,
          action: () => window.open( discord.value.path, '_blank' )
        }, {
          label: versionLabel.value,
          visible: true,
          displayOnly: true
        } ]
      }
    ];
  }
);

const menuPtStyle = {
  submenuLabel: "border-t first:border-t-0 border-neutral-600 mx-2 mt-3 pt-5 first:pt-0 pb-2 opacity-80 uppercase text-xs font-light tracking-wider",
  itemcontent: "m-0",
  item: { id: "menu-item" }
};
</script>

<template>
  <div class="bg-base-100">
    <div class="p-2">
      <router-link :to="routeManager.homePath()"
                   class="tmi-logo max-w-32 pb-2 h-9 block opacity-80 hover:opacity-100 transition-opacity duration-200">

      </router-link>
    </div>
    <template v-for="menu in navigation">
      <div v-if="menu.visible" :id="menu.id">
        <div v-if="menu.label" :class="menuPtStyle.submenuLabel">{{ menu.label }}</div>
        <Menu :model="menu.items" unstyled :pt="menuPtStyle">
          <template #item="{ item }">
            <SidebarRouterMenuItem v-if="item.visible && item.route" :item="item"></SidebarRouterMenuItem>
            <SidebarActionMenuItem v-else-if="item.visible && item.action" :item="item"></SidebarActionMenuItem>
            <SidebarLabelMenuItem v-else-if="item.visible && item.displayOnly" :item="item"></SidebarLabelMenuItem>
          </template>
        </Menu>
      </div>
    </template>
  </div>
  <SignoutModal ref="signoutModal"></SignoutModal>
</template>